import { ref } from 'vue'
import { createChart, LineSeries } from 'lightweight-charts'

// ─── Reference line prices — fill in PMP_SPOT & HEDGE_AVG ──────
export const PMP_SPOT  = 80000   // manualNotional / manual_qty_sum
export const HEDGE_AVG = 78000   // hedgeNotional / short_qty_sum
// ─────────────────────────────────────────────────────────────────

// ─── Initial zoom control ────────────────────────────────────────
// VISIBLE_BARS  : how many 1h candles are visible at once (lower = more zoom)
// RIGHT_SPACE   : empty bars to the right so the latest price sits near center
const VISIBLE_BARS  = 48   // 48h window
const RIGHT_SPACE   = 16   // bars of empty space to the right
// ─────────────────────────────────────────────────────────────────

const CHART_OPTS = {
  autoSize: true,
  layout: {
    background: { color: 'var(--chart-bg, #161b22)' },
    textColor: '#c9d1d9',
    fontSize: 14,
  },
  grid: {
    vertLines: { color: '#21262d' },
    horzLines: { color: '#21262d' },
  },
  rightPriceScale: { borderColor: '#30363d' },
  timeScale: { borderColor: '#30363d', timeVisible: true, secondsVisible: false },
  crosshair: { mode: 1 },
}

export function useChart(containerRef) {
  const chart        = ref(null)
  const series       = ref(null)
  const currentPrice = ref(null)   // set from last kline close during init()
  const _pmpLine     = ref(null)
  const _hedgeLine   = ref(null)

  async function fetchKlines() {
    const url = 'https://api.binance.com/api/v3/klines?symbol=BTCUSDC&interval=1h&limit=168'
    const res  = await fetch(url)
    const raw  = await res.json()
    return raw.map(k => ({ time: Math.floor(k[0] / 1000), value: parseFloat(k[4]) }))
  }

  async function init(pmpSpot, hedgeAvgPrice) {
    chart.value  = createChart(containerRef.value, CHART_OPTS)
    series.value = chart.value.addSeries(LineSeries, { color: '#58a6ff', lineWidth: 2 })

    const klines = await fetchKlines()
    series.value.setData(klines)

    // Derive current price from last candle close
    currentPrice.value = klines[klines.length - 1].value

    // Fixed reference lines
    series.value.createPriceLine({ price: currentPrice.value, color: '#3498db', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: 'Price' })
    _pmpLine.value   = series.value.createPriceLine({ price: pmpSpot   || PMP_SPOT,  color: '#f39c12', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: 'PMP' })
    _hedgeLine.value = series.value.createPriceLine({ price: hedgeAvgPrice || HEDGE_AVG, color: '#9b59b6', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: 'HedgeAvg' })

    // Center the latest price: leave RIGHT_SPACE empty bars on the right
    const lastIdx = klines.length - 1
    chart.value.timeScale().setVisibleLogicalRange({
      from: lastIdx - VISIBLE_BARS + RIGHT_SPACE,
      to:   lastIdx + RIGHT_SPACE,
    })
  }

  // Call after init() to add the simulation price lines on the chart.
  // lowerPrices → yellow, upperPrices → cyan, so they are visually distinct.
  function createSimLines(lowerPrices, upperPrices) {
    const mkLine = (price, color) =>
      series.value.createPriceLine({
        price,
        color,
        lineWidth: 1,
        lineStyle: 3, // dashed
        axisLabelVisible: false,
      })
    return [
      ...lowerPrices.map(p => mkLine(p, 'rgba(243, 156, 18, 0.7)')),   // orange — buy zones
      ...upperPrices.map(p => mkLine(p, 'rgba(46, 204, 113, 0.7)')),   // green  — sell zones
    ]
  }

  function updatePmpLine(price) {
    if (_pmpLine.value && price > 0) _pmpLine.value.applyOptions({ price })
  }

  function updateHedgeLine(price) {
    if (_hedgeLine.value && price > 0) _hedgeLine.value.applyOptions({ price })
  }

  function destroy() {
    if (chart.value) { chart.value.remove(); chart.value = null }
  }

  return { chart, series, currentPrice, init, createSimLines, destroy, updatePmpLine, updateHedgeLine }
}
